import React, { useEffect, useState } from 'react';
import { workstreamAPI } from '../services/api';
import { formatDateBritish, formatDateTimeBritish } from '../utils/dateUtils';
import './VehicleList.css';

interface ApprovalNotice {
  id: number;
  vehicleId: number;
  vehicleRegistration: string;
  vehicleMake: string;
  vehicleModel: string;
  action: string;
  note: string;
  adminName: string;
  dateSubmitted: string;
  dateApproved: string;
  clientId: number;
  coverStatus?: string;
  coverEffectiveDate?: string;
  isRead: boolean;
  readAt?: string;
  readBy?: string;
}

// Helper function to deduplicate notices - keep only latest per registration
const deduplicateByRegistration = (notices: NoticeDisplay[]): NoticeDisplay[] => {
  const registrationMap = new Map<string, NoticeDisplay>();
  
  // Sort by dateApproved descending (newest first)
  const sorted = [...notices].sort((a, b) => 
    new Date(b.dateApproved).getTime() - new Date(a.dateApproved).getTime()
  );
  
  // Keep only the first (most recent) occurrence of each registration
  sorted.forEach(notice => {
    const registration = notice.vehicleDetails.split(' - ')[0]; // Extract registration from "REG - Make Model"
    if (!registrationMap.has(registration)) {
      registrationMap.set(registration, notice);
    }
  });
  
  // Return deduplicated notices sorted by date (newest first)
  return Array.from(registrationMap.values()).sort((a, b) => 
    new Date(b.dateApproved).getTime() - new Date(a.dateApproved).getTime()
  );
};

interface NoticeDisplay {
  id: number;
  vehicleId: number;
  vehicleDetails: string;
  action: string;
  note: string;
  adminName: string;
  dateSubmitted: string;
  dateApproved: string;
  coverStatus?: string;
  coverEffectiveDate?: string;
  isRead: boolean;
  readAt?: string;
  readBy?: string;
}

const CoverNotices: React.FC = () => {
  const [recentNotices, setRecentNotices] = useState<NoticeDisplay[]>([]);
  const [allNotices, setAllNotices] = useState<NoticeDisplay[]>([]);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [selectedNotices, setSelectedNotices] = useState<number[]>([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');


  useEffect(() => {
    loadNotices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadNotices = async () => {
    try {
      // Load last 30 days
      const recentResponse = await workstreamAPI.getApprovals(user.clientId);
      const recent = recentResponse.data.map((notice: ApprovalNotice) => ({
        id: notice.id,
        vehicleId: notice.vehicleId,
        vehicleDetails: `${notice.vehicleRegistration} - ${notice.vehicleMake} ${notice.vehicleModel}`,
        action: notice.action,
        note: notice.note,
        adminName: notice.adminName,
        dateSubmitted: notice.dateSubmitted,
        dateApproved: notice.dateApproved,
        coverStatus: notice.coverStatus,
        coverEffectiveDate: notice.coverEffectiveDate,
        isRead: notice.isRead,
        readAt: notice.readAt,
        readBy: notice.readBy
      }));
      // Deduplicate to show only latest status per registration
      setRecentNotices(deduplicateByRegistration(recent));

      // Load all history
      const allResponse = await workstreamAPI.getAllApprovals(user.clientId);
      const all = allResponse.data.map((notice: ApprovalNotice) => ({
        id: notice.id,
        vehicleId: notice.vehicleId,
        vehicleDetails: `${notice.vehicleRegistration} - ${notice.vehicleMake} ${notice.vehicleModel}`,
        action: notice.action,
        note: notice.note,
        adminName: notice.adminName,
        dateSubmitted: notice.dateSubmitted,
        dateApproved: notice.dateApproved,
        coverStatus: notice.coverStatus,
        coverEffectiveDate: notice.coverEffectiveDate,
        isRead: notice.isRead,
        readAt: notice.readAt,
        readBy: notice.readBy
      }));
      // Deduplicate to show only latest status per registration
      setAllNotices(deduplicateByRegistration(all));
    } catch (error) {
      console.error('Error loading notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarking(true);
      await workstreamAPI.markAsRead(undefined, true);
      setSelectedNotices([]);
      await loadNotices();
    } catch (error) {
      console.error('Error marking notices as read:', error);
      alert('Failed to mark notices as read');
    } finally {
      setMarking(false);
    }
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedNotices.length === 0) return;
    try {
      setMarking(true);
      await workstreamAPI.markAsRead(selectedNotices, false);
      setSelectedNotices([]);
      await loadNotices();
    } catch (error) {
      console.error('Error marking notices as read:', error);
      alert('Failed to mark notices as read');
    } finally {
      setMarking(false);
    }
  };

  const handleToggleNotice = (noticeId: number) => {
    setSelectedNotices(prev => 
      prev.includes(noticeId) 
        ? prev.filter(id => id !== noticeId)
        : [...prev, noticeId]
    );
  };

  const handleToggleAll = () => {
    const unreadNotices = displayNotices.filter(n => !n.isRead);
    if (selectedNotices.length === unreadNotices.length) {
      setSelectedNotices([]);
    } else {
      setSelectedNotices(unreadNotices.map(n => n.id));
    }
  };

  if (loading) {
    return <div>Loading cover notices...</div>;
  }

  const displayNotices = showAllHistory ? allNotices : recentNotices;

  return (
    <div className="vehicle-list">
      <div className="page-header">
        <h1>Cover Notices & Messages</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            className={`btn ${!showAllHistory ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowAllHistory(false)}
          >
            Last 30 Days ({recentNotices.length})
          </button>
          <button 
            className={`btn ${showAllHistory ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowAllHistory(true)}
          >
            Full History ({allNotices.length})
          </button>
          <button 
            className="btn btn-success"
            onClick={handleMarkSelectedAsRead}
            disabled={marking || selectedNotices.length === 0}
          >
            {marking ? 'Marking...' : `Mark Selected as Read (${selectedNotices.length})`}
          </button>
          <button 
            className="btn btn-success"
            onClick={handleMarkAllAsRead}
            disabled={marking || recentNotices.filter(n => !n.isRead).length === 0}
          >
            {marking ? 'Marking...' : 'Mark All as Read'}
          </button>
        </div>
      </div>

      <div className="card">
        {displayNotices.length === 0 ? (
          <p className="empty-state">
            {showAllHistory 
              ? 'No approval history found.' 
              : 'No approvals in the last 30 days.'}
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input 
                    type="checkbox" 
                    onChange={handleToggleAll}
                    checked={selectedNotices.length > 0 && selectedNotices.length === displayNotices.filter(n => !n.isRead).length}
                  />
                </th>
                <th>Vehicle</th>
                <th>Status</th>
                <th>Date Submitted</th>
                <th>Date Approved/Rejected</th>
                <th>Processed By</th>
                <th>Notes</th>
                <th>Read Status</th>
              </tr>
            </thead>
            <tbody>
              {displayNotices.map((notice) => (
                <tr key={notice.id} style={{ backgroundColor: notice.isRead ? '#f8f9fa' : 'white' }}>
                  <td>
                    <input 
                      type="checkbox" 
                      checked={selectedNotices.includes(notice.id)}
                      onChange={() => handleToggleNotice(notice.id)}
                      disabled={notice.isRead}
                    />
                  </td>
                  <td><strong>{notice.vehicleDetails}</strong></td>
                  <td>
                    {(() => {
                      // Determine display status based on coverStatus
                      let displayStatus = 'Pending Cover';
                      let bgColor = '#FFA500'; // Amber
                      let tooltip = '';
                      
                      if (notice.coverStatus === 'Active') {
                        displayStatus = 'On Cover';
                        bgColor = '#28a745'; // Green
                        if (notice.coverEffectiveDate) {
                          tooltip = `Cover started: ${formatDateBritish(notice.coverEffectiveDate)} at 00:00 (midnight)`;
                        }
                      } else if (notice.coverStatus === 'Cover Pending') {
                        displayStatus = 'Cover Pending';
                        bgColor = '#FFA500'; // Amber
                        if (notice.coverEffectiveDate) {
                          tooltip = `Cover starts: ${formatDateBritish(notice.coverEffectiveDate)} at 00:00 (midnight)`;
                        }
                      } else if (notice.action === 'Pending Removal') {
                        displayStatus = 'Pending Removal';
                        bgColor = '#b45309'; // Amber
                      } else if (notice.coverStatus === 'Inactive' || notice.action === 'Rejected' || notice.action === 'Removed from Cover') {
                        displayStatus = 'Inactive';
                        bgColor = '#dc3545'; // Red
                        if (notice.action === 'Removed from Cover' || notice.action === 'Rejected') {
                          tooltip = `Cover stopped: ${formatDateBritish(notice.dateApproved)}`;
                        }
                      }
                      
                      return (
                        <>
                          <span 
                            className="status-badge"
                            title={tooltip}
                            style={{ 
                              backgroundColor: bgColor,
                              color: 'white',
                              padding: '3px 8px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '500',
                              cursor: tooltip ? 'help' : 'default',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {displayStatus}
                            {tooltip && <span style={{ fontSize: '10px' }}>ⓘ</span>}
                          </span>
                          {notice.action === 'Pending Removal' && (
                            <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#b45309', marginTop: 3 }}>
                              Awaiting Yourshield action
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td>{formatDateBritish(notice.dateSubmitted)}</td>
                  <td>{formatDateBritish(notice.dateApproved)}</td>
                  <td>{notice.adminName}</td>
                  <td style={{ maxWidth: '300px' }}>
                    {notice.note || <span style={{ color: '#718096', fontStyle: 'italic' }}>No notes</span>}
                  </td>
                  <td>
                    {notice.isRead ? (
                      <div style={{ fontSize: '12px', color: '#28a745' }}>
                        ✓ Read<br />
                        {notice.readAt && (
                          <span style={{ color: '#718096' }}>
                            {formatDateTimeBritish(notice.readAt)}<br />
                            by {notice.readBy}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#FFA500', fontWeight: '500' }}>Unread</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CoverNotices;
